import { useCallback, useEffect, useRef, useState } from "react"
import confetti from "canvas-confetti"
import { ArrowRight, Clock3, PartyPopper, RotateCcw, Trophy, XCircle } from "lucide-react"
import api from "@/lib/api"
import { getErrorMessage, toastError } from "@/lib/toast"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

// Fase 1 de openspec/changes/section-quiz-question-types — 10 tipos de
// texto/inputs. La forma de `content`/`correct_answer`/`answer` depende de
// `question_type`, igual que en la API (ver
// platforms/api/src/lib/section-quiz/question-types.ts).
type QuestionType =
  | "multiple_choice" | "multiple_select" | "true_false" | "short_answer" | "essay"
  | "fill_blank" | "numerical" | "code" | "matching" | "ordering"

interface AttemptQuestion {
  index: number
  question_text: string
  question_type: QuestionType
  content: Record<string, unknown>
}

interface ReviewQuestion extends AttemptQuestion {
  correct_answer: unknown
  explanation?: string | null
  answer: unknown
  is_correct: boolean
}

type ViewState =
  | { step: "loading" }
  | { step: "error"; message: string }
  | { step: "cooldown"; retryAvailableAt: string; requiresTeam?: boolean }
  | { step: "alreadyPassed"; bestScore: number }
  | { step: "answering"; attemptId: string; questions: AttemptQuestion[] }
  | { step: "result"; score: number; passed: boolean; questions: ReviewQuestion[]; retryAvailableAt: string | null; firstTimePassed: boolean }

const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  multiple_choice: "Opción múltiple",
  multiple_select: "Selección múltiple",
  true_false: "Verdadero o falso",
  short_answer: "Respuesta corta",
  essay: "Ensayo",
  fill_blank: "Completar espacios",
  numerical: "Numérica",
  code: "Código",
  matching: "Emparejar",
  ordering: "Ordenar",
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : []
}

/** True cuando el valor "en bruto" de la respuesta cumple la forma mínima esperada por el tipo. */
function isAnswered(type: QuestionType, content: Record<string, unknown>, raw: unknown): boolean {
  switch (type) {
    case "multiple_choice":
      return typeof raw === "number"
    case "multiple_select":
      return Array.isArray(raw) && raw.length > 0
    case "true_false":
      return typeof raw === "boolean"
    case "short_answer":
    case "essay":
    case "code":
      return typeof raw === "string" && raw.trim().length > 0
    case "fill_blank": {
      const blankCount = (String(content.textWithBlanks ?? "").match(/___/g) ?? []).length
      return Array.isArray(raw) && raw.length === blankCount && raw.every((s) => typeof s === "string" && s.trim().length > 0)
    }
    case "numerical":
      return typeof raw === "number" && !Number.isNaN(raw)
    case "matching": {
      const leftCount = asStringArray(content.left).length
      return Array.isArray(raw) && raw.length === leftCount && raw.every((v) => typeof v === "number")
    }
    case "ordering": {
      const itemCount = asStringArray(content.items).length
      return Array.isArray(raw) && raw.length === itemCount && raw.every((v) => typeof v === "number") && new Set(raw as number[]).size === itemCount
    }
    default:
      return false
  }
}

/** Convierte el valor "en bruto" que maneja la UI a la forma que espera el backend. Solo `ordering` difiere. */
function toSubmittedAnswer(type: QuestionType, raw: unknown): unknown {
  if (type === "ordering" && Array.isArray(raw)) {
    // raw[itemIndex] = posición 1-based elegida para ese ítem → convertir a
    // permutación donde result[position] = itemIndex, igual que correct_answer.
    const positions = raw as number[]
    const result = new Array(positions.length).fill(-1)
    positions.forEach((pos, itemIndex) => {
      if (pos >= 1 && pos <= positions.length) result[pos - 1] = itemIndex
    })
    return result
  }
  return raw
}

/** "3h 12m" / "45m" countdown text, ticking every second until it hits zero. */
function useCountdown(targetIso: string | null) {
  const [label, setLabel] = useState<string | null>(null)
  const [reached, setReached] = useState(false)

  useEffect(() => {
    if (!targetIso) {
      setLabel(null)
      setReached(false)
      return
    }
    const target = new Date(targetIso).getTime()
    const tick = () => {
      const remainingMs = target - Date.now()
      if (remainingMs <= 0) {
        setLabel("0m")
        setReached(true)
        return
      }
      const hours = Math.floor(remainingMs / (60 * 60 * 1000))
      const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000))
      setLabel(hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`)
      setReached(false)
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [targetIso])

  return { label, reached }
}

/** The "Fireworks" preset from https://confetti.js.org/ — repeated random-position bursts for a few seconds. */
function launchFireworksConfetti() {
  const duration = 3000
  const end = Date.now() + duration
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#a855f7"]

  ;(function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    })
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    })
    if (Date.now() < end) requestAnimationFrame(frame)
  })()
}

const optionRowCls = (active: boolean) =>
  `flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
    active ? "border-primary bg-primary/10" : "border-border hover:bg-foreground/5"
  }`
const textInputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
const selectCls = "rounded-lg border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"

/** Renders the answer input widget for one question, during the "answering" step. */
function QuestionAnswerInput({ q, value, onChange }: { q: AttemptQuestion; value: unknown; onChange: (v: unknown) => void }) {
  const content = q.content ?? {}

  switch (q.question_type) {
    case "multiple_choice": {
      const options = asStringArray(content.options)
      return (
        <div className="flex flex-col gap-2">
          {options.map((option, optIdx) => (
            <label key={optIdx} className={optionRowCls(value === optIdx)}>
              <input type="radio" name={`question-${q.index}`} className="accent-primary"
                checked={value === optIdx} onChange={() => onChange(optIdx)} />
              {option}
            </label>
          ))}
        </div>
      )
    }

    case "multiple_select": {
      const options = asStringArray(content.options)
      const selected = new Set(Array.isArray(value) ? (value as number[]) : [])
      return (
        <div className="flex flex-col gap-2">
          {options.map((option, optIdx) => (
            <label key={optIdx} className={optionRowCls(selected.has(optIdx))}>
              <input type="checkbox" className="accent-primary" checked={selected.has(optIdx)}
                onChange={(e) => {
                  const next = new Set(selected)
                  if (e.target.checked) next.add(optIdx); else next.delete(optIdx)
                  onChange(Array.from(next).sort((a, b) => a - b))
                }} />
              {option}
            </label>
          ))}
        </div>
      )
    }

    case "true_false": {
      return (
        <div className="flex gap-2">
          {[true, false].map((v) => (
            <label key={String(v)} className={`${optionRowCls(value === v)} flex-1 justify-center`}>
              <input type="radio" name={`question-${q.index}`} className="accent-primary"
                checked={value === v} onChange={() => onChange(v)} />
              {v ? "Verdadero" : "Falso"}
            </label>
          ))}
        </div>
      )
    }

    case "short_answer":
      return (
        <input type="text" className={textInputCls} placeholder="Escribe tu respuesta"
          value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
      )

    case "essay":
      return (
        <textarea className={`${textInputCls} min-h-28 resize-y`} placeholder="Escribe tu respuesta"
          value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
      )

    case "code":
      return (
        <textarea className={`${textInputCls} min-h-28 resize-y font-mono text-xs`} placeholder="Escribe tu código"
          value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
      )

    case "numerical":
      return (
        <input type="number" className={textInputCls} placeholder="Escribe un número"
          value={typeof value === "number" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />
      )

    case "fill_blank": {
      const text = String(content.textWithBlanks ?? "")
      const parts = text.split("___")
      const blanks = Array.isArray(value) ? (value as string[]) : []
      return (
        <p className="flex flex-wrap items-center gap-1.5 text-sm leading-8 text-foreground">
          {parts.map((part, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {part}
              {i < parts.length - 1 && (
                <input type="text" className="w-28 rounded border border-border bg-background px-2 py-0.5 text-sm outline-none focus:border-primary"
                  value={blanks[i] ?? ""}
                  onChange={(e) => {
                    const next = [...blanks]
                    next[i] = e.target.value
                    onChange(next)
                  }} />
              )}
            </span>
          ))}
        </p>
      )
    }

    case "matching": {
      const left = asStringArray(content.left)
      const right = asStringArray(content.right)
      const chosen = Array.isArray(value) ? (value as number[]) : []
      return (
        <div className="flex flex-col gap-2">
          {left.map((label, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
              <span className="text-sm text-foreground">{label}</span>
              <select className={selectCls} value={chosen[i] ?? ""}
                onChange={(e) => {
                  const next = [...chosen]
                  next[i] = Number(e.target.value)
                  onChange(next)
                }}>
                <option value="" disabled>Elige…</option>
                {right.map((opt, j) => (
                  <option key={j} value={j}>{opt}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )
    }

    case "ordering": {
      const items = asStringArray(content.items)
      const positions = Array.isArray(value) ? (value as number[]) : []
      return (
        <div className="flex flex-col gap-2">
          {items.map((label, itemIdx) => (
            <div key={itemIdx} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
              <span className="text-sm text-foreground">{label}</span>
              <select className={selectCls} value={positions[itemIdx] ?? ""}
                onChange={(e) => {
                  const next = [...positions]
                  next[itemIdx] = Number(e.target.value)
                  onChange(next)
                }}>
                <option value="" disabled>Posición…</option>
                {items.map((_, pos) => (
                  <option key={pos} value={pos + 1}>{pos + 1}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )
    }

    default:
      return null
  }
}

function Chip({ tone, children }: { tone: "correct" | "wrong" | "neutral"; children: React.ReactNode }) {
  const cls =
    tone === "correct" ? "border-green-500/60 bg-green-500/10"
      : tone === "wrong" ? "border-red-500/60 bg-red-500/10"
      : "border-border"
  return <li className={`rounded-lg border px-3 py-2 text-sm ${cls}`}>{children}</li>
}

/** Renders the graded content + correct answer + student answer, during the "result" step. */
function ReviewQuestionBody({ q }: { q: ReviewQuestion }) {
  const content = q.content ?? {}

  switch (q.question_type) {
    case "multiple_choice": {
      const options = asStringArray(content.options)
      const correctIdx = Number(q.correct_answer)
      const answerIdx = typeof q.answer === "number" ? q.answer : null
      return (
        <ul className="flex flex-col gap-1">
          {options.map((option, i) => (
            <Chip key={i} tone={i === correctIdx ? "correct" : i === answerIdx ? "wrong" : "neutral"}>{option}</Chip>
          ))}
        </ul>
      )
    }
    case "multiple_select": {
      const options = asStringArray(content.options)
      const correctSet = new Set(Array.isArray(q.correct_answer) ? (q.correct_answer as number[]) : [])
      const answerSet = new Set(Array.isArray(q.answer) ? (q.answer as number[]) : [])
      return (
        <ul className="flex flex-col gap-1">
          {options.map((option, i) => (
            <Chip key={i} tone={correctSet.has(i) ? "correct" : answerSet.has(i) ? "wrong" : "neutral"}>{option}</Chip>
          ))}
        </ul>
      )
    }
    case "true_false": {
      const correct = Boolean(q.correct_answer)
      const answer = typeof q.answer === "boolean" ? q.answer : null
      return (
        <ul className="flex gap-2">
          {[true, false].map((v) => (
            <Chip key={String(v)} tone={v === correct ? "correct" : v === answer ? "wrong" : "neutral"}>{v ? "Verdadero" : "Falso"}</Chip>
          ))}
        </ul>
      )
    }
    case "short_answer":
      return (
        <div className="space-y-1 text-sm">
          <p>Tu respuesta: <span className="font-medium text-foreground">{typeof q.answer === "string" && q.answer ? q.answer : "(sin respuesta)"}</span></p>
          <p className="text-xs text-muted-foreground">Aceptadas: {asStringArray(q.correct_answer).join(" · ")}</p>
        </div>
      )
    case "essay":
    case "code":
      return (
        <div className="space-y-1 text-sm">
          <p className="whitespace-pre-wrap">Tu respuesta: <span className="font-medium text-foreground">{typeof q.answer === "string" && q.answer ? q.answer : "(sin respuesta)"}</span></p>
          <p className="text-xs text-muted-foreground">Criterio de corrección: {String(q.correct_answer)}</p>
        </div>
      )
    case "fill_blank": {
      const correctBlanks = asStringArray(q.correct_answer)
      const answerBlanks = asStringArray(q.answer)
      return (
        <ul className="flex flex-wrap gap-1.5">
          {correctBlanks.map((correct, i) => {
            const ok = (answerBlanks[i] ?? "").trim().toLowerCase() === correct.trim().toLowerCase()
            return (
              <Chip key={i} tone={ok ? "correct" : "wrong"}>
                {answerBlanks[i] || "(vacío)"} {!ok && `(correcta: ${correct})`}
              </Chip>
            )
          })}
        </ul>
      )
    }
    case "numerical": {
      const ca = (q.correct_answer ?? {}) as { value?: number; tolerance?: number }
      return (
        <p className="text-sm">
          Tu respuesta: <span className="font-medium text-foreground">{q.answer === null || q.answer === undefined ? "(sin respuesta)" : String(q.answer)}</span>
          {" · "}
          <span className="text-xs text-muted-foreground">Correcta: {ca.value}{ca.tolerance ? ` ± ${ca.tolerance}` : ""}</span>
        </p>
      )
    }
    case "matching": {
      const left = asStringArray(content.left)
      const right = asStringArray(content.right)
      const correct = Array.isArray(q.correct_answer) ? (q.correct_answer as number[]) : []
      const answer = Array.isArray(q.answer) ? (q.answer as number[]) : []
      return (
        <ul className="flex flex-col gap-1">
          {left.map((label, i) => {
            const ok = answer[i] === correct[i]
            return (
              <Chip key={i} tone={ok ? "correct" : "wrong"}>
                {label} → {answer[i] !== undefined ? right[answer[i]] ?? "?" : "(sin respuesta)"}
                {!ok && ` (correcta: ${right[correct[i]]})`}
              </Chip>
            )
          })}
        </ul>
      )
    }
    case "ordering": {
      const items = asStringArray(content.items)
      const correct = Array.isArray(q.correct_answer) ? (q.correct_answer as number[]) : []
      const answer = Array.isArray(q.answer) ? (q.answer as number[]) : []
      return (
        <div className="space-y-1 text-sm">
          <p>Tu orden: {answer.length ? answer.map((i) => items[i]).join(" → ") : "(sin respuesta)"}</p>
          <p className="text-xs text-muted-foreground">Orden correcto: {correct.map((i) => items[i]).join(" → ")}</p>
        </div>
      )
    }
    default:
      return null
  }
}

export default function SectionQuizView({
  sectionQuizId,
  onExit,
  hasNextLesson = false,
  onNavigateNext,
}: {
  sectionQuizId: string
  onExit: () => void
  hasNextLesson?: boolean
  onNavigateNext?: () => void
}) {
  const [state, setState] = useState<ViewState>({ step: "loading" })
  const [answers, setAnswers] = useState<Record<number, unknown>>({})
  // Guards against a stale in-flight attempt (e.g. React StrictMode's
  // double-invoked effect, or a fast re-trigger) overwriting state set by a
  // more recent call — only the latest call's response is ever applied.
  const requestIdRef = useRef(0)
  const firedConfettiRef = useRef(false)

  const beginNewAttempt = useCallback(async (requestId: number) => {
    const res = await api.post(`/academy/section-quizzes/${sectionQuizId}/attempts`)
    if (requestIdRef.current !== requestId) return
    const { id, questions } = res.data.data
    setState({ step: "answering", attemptId: id, questions })
  }, [sectionQuizId])

  /** Mounts the exam: shows the already-passed screen if applicable, otherwise the usual cooldown/answering flow. */
  const startAttempt = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setState({ step: "loading" })
    setAnswers({})
    try {
      const cooldown = await api.get(`/academy/section-quizzes/${sectionQuizId}/cooldown-status`)
      if (requestIdRef.current !== requestId) return
      if (cooldown.data.data.already_passed) {
        setState({ step: "alreadyPassed", bestScore: cooldown.data.data.best_score })
        return
      }
      if (!cooldown.data.data.can_attempt) {
        setState({
          step: "cooldown",
          retryAvailableAt: cooldown.data.data.retry_available_at ?? new Date().toISOString(),
          requiresTeam: Boolean(cooldown.data.data.requires_team),
        })
        return
      }

      await beginNewAttempt(requestId)
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      setState({ step: "error", message: getErrorMessage(err) })
    }
  }, [sectionQuizId, beginNewAttempt])

  /** "Intentar de nuevo para mejorar tu puntaje" from the already-passed screen — skips straight to a fresh attempt. */
  const retryToImprove = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setState({ step: "loading" })
    setAnswers({})
    try {
      await beginNewAttempt(requestId)
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      setState({ step: "error", message: getErrorMessage(err) })
    }
  }, [beginNewAttempt])

  useEffect(() => {
    void startAttempt()
  }, [startAttempt])

  const handleSubmit = useCallback(async () => {
    if (state.step !== "answering") return
    const attemptId = state.attemptId
    try {
      const submit = await api.post(`/academy/section-quizzes/attempts/${attemptId}/submit`, {
        answers: state.questions.map((q) => ({
          question_index: q.index,
          answer: toSubmittedAnswer(q.question_type, answers[q.index]),
        })),
      })
      const review = await api.get(`/academy/section-quizzes/attempts/${attemptId}`)
      setState({
        step: "result",
        score: review.data.data.score,
        passed: review.data.data.passed,
        questions: review.data.data.questions,
        retryAvailableAt: submit.data.data.retry_available_at ?? null,
        firstTimePassed: Boolean(submit.data.data.first_time_passed),
      })
    } catch (err) {
      toastError(err)
    }
  }, [state, answers])

  const { label: cooldownLabel, reached: cooldownReached } =
    useCountdown(state.step === "cooldown" ? state.retryAvailableAt : state.step === "result" && !state.passed ? state.retryAvailableAt : null)

  useEffect(() => {
    if (state.step === "result" && state.firstTimePassed && !firedConfettiRef.current) {
      firedConfettiRef.current = true
      launchFireworksConfetti()
    }
    if (state.step !== "result") firedConfettiRef.current = false
  }, [state])

  const totalQuestions = state.step === "answering" ? state.questions.length : 0
  const answeredCount = state.step === "answering"
    ? state.questions.filter((q) => isAnswered(q.question_type, q.content, answers[q.index])).length
    : 0
  const progressPercent = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col gap-6 px-4 py-10">
      <button type="button" onClick={onExit} className="self-start text-sm text-muted-foreground hover:text-foreground">
        ← Volver al curso
      </button>

      {state.step === "loading" && (
        <div role="status" className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Preparando tu examen...</p>
        </div>
      )}

      {state.step === "error" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <p className="text-sm text-foreground">{state.message}</p>
          <Button onClick={() => void startAttempt()}>Reintentar</Button>
        </div>
      )}

      {state.step === "cooldown" && (
        <Card className="items-center gap-4 border-none bg-muted/40 px-6 py-12 text-center shadow-none ring-0">
          <CardContent className="flex flex-col items-center gap-4">
            <div className="flex size-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
              <Clock3 className="size-8" />
            </div>
            <div className="space-y-1">
              <h1 className="text-xl font-bold text-foreground">
                {state.requiresTeam ? "Este examen es por equipo" : "Aún no puedes reintentar"}
              </h1>
              <p className="max-w-sm text-sm text-muted-foreground">
                {state.requiresTeam
                  ? "Este examen se responde en equipo (CCCD) y no perteneces a ninguno todavía."
                  : "Para asegurarte de repasar el contenido, puedes volver a intentarlo cuando pase el tiempo de espera."}
              </p>
            </div>
            {!state.requiresTeam && (
              <Badge variant="secondary" className="text-sm">
                {cooldownReached ? "Ya puedes reintentar" : `Podrás reintentar en ${cooldownLabel ?? "..."}`}
              </Badge>
            )}
            {cooldownReached && !state.requiresTeam && (
              <Button onClick={() => void startAttempt()}>Reintentar ahora</Button>
            )}
          </CardContent>
        </Card>
      )}

      {state.step === "alreadyPassed" && (
        <Card className="items-center gap-4 border-none bg-green-500/10 px-6 py-12 text-center shadow-none ring-0">
          <CardContent className="flex flex-col items-center gap-4">
            <div className="flex size-16 items-center justify-center rounded-full bg-green-500/10 text-green-500">
              <Trophy className="size-8" />
            </div>
            <div className="space-y-1">
              <h1 className="text-xl font-bold text-foreground">¡Felicitaciones! Ya aprobaste</h1>
              <Badge variant="default" className="text-sm">{state.bestScore}%</Badge>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {hasNextLesson && (
                <Button size="lg" onClick={onNavigateNext}>
                  Ir a la siguiente lección <ArrowRight className="size-4" />
                </Button>
              )}
              <Button size="lg" variant={hasNextLesson ? "outline" : "default"} onClick={() => void retryToImprove()}>
                <RotateCcw className="size-4" /> Intentar de nuevo para mejorar tu puntaje
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {state.step === "answering" && (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-foreground">Examen de la sección</h1>
              <Badge variant="outline">{answeredCount}/{totalQuestions}</Badge>
            </div>
            <div role="progressbar" aria-label="Preguntas respondidas" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressPercent)} className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full w-full rounded-full bg-primary motion-progress motion-progress-in" style={{ transform: `scaleX(${progressPercent / 100})` }} />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {state.questions.map((q) => (
              <Card key={q.index} className="px-4">
                <CardContent className="px-0">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="font-medium text-foreground">{q.index + 1}. {q.question_text}</p>
                    <Badge variant="outline" className="shrink-0 text-micro">{QUESTION_TYPE_LABEL[q.question_type]}</Badge>
                  </div>
                  <QuestionAnswerInput q={q} value={answers[q.index]} onChange={(v) => setAnswers((a) => ({ ...a, [q.index]: v }))} />
                </CardContent>
              </Card>
            ))}
          </div>
          <Button size="lg" disabled={answeredCount < totalQuestions} onClick={() => void handleSubmit()} className="self-start">
            Enviar respuestas
          </Button>
        </>
      )}

      {state.step === "result" && (
        <>
          <Card className={`items-center gap-3 border-none px-6 py-10 text-center shadow-none ring-0 ${state.passed ? "bg-green-500/10" : "bg-red-500/10"}`}>
            <CardContent className="flex flex-col items-center gap-3">
              {state.passed ? (
                <PartyPopper className="size-10 text-green-500" />
              ) : (
                <XCircle className="size-10 text-red-500" />
              )}
              <h1 className="text-2xl font-bold text-foreground">
                {state.passed ? "¡Aprobaste!" : "No aprobaste"}
              </h1>
              <Badge variant={state.passed ? "default" : "destructive"} className="text-sm">{state.score}%</Badge>
              {!state.passed && (
                <p className="text-sm text-muted-foreground">
                  {cooldownReached ? "Ya puedes intentarlo de nuevo." : `Podrás reintentar en ${cooldownLabel ?? "..."}`}
                </p>
              )}
              {state.passed && hasNextLesson && (
                <Button size="lg" onClick={onNavigateNext} className="mt-2">
                  Ir a la siguiente lección <ArrowRight className="size-4" />
                </Button>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-4">
            {state.questions.map((q) => (
              <Card key={q.index} className="px-4">
                <CardContent className="px-0">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="font-medium text-foreground">{q.index + 1}. {q.question_text}</p>
                    <Badge variant="outline" className="shrink-0 text-micro">{QUESTION_TYPE_LABEL[q.question_type]}</Badge>
                  </div>
                  <ReviewQuestionBody q={q} />
                  {q.explanation && <p className="mt-2 text-xs text-muted-foreground">{q.explanation}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
          {!state.passed && cooldownReached && (
            <Button onClick={() => void startAttempt()} className="self-start">
              <RotateCcw className="size-4" /> Intentar de nuevo
            </Button>
          )}
        </>
      )}
    </div>
  )
}
